import * as XLSX from 'xlsx-js-style';

// App theme colors
const PRIMARY = '2563EB';
const PRIMARY_LIGHT = 'EFF6FF';
const ALT_ROW = 'F0F4FF';
const BORDER_COLOR = 'D1D5DB';
const TOTAL_BG = 'E5E7EB';
const PARAM_LABEL = '475569';
const WHITE = 'FFFFFF';

const thin = { style: 'thin', color: { rgb: BORDER_COLOR } };
const border = { top: thin, bottom: thin, left: thin, right: thin };

const headerStyle = {
  font: { bold: true, color: { rgb: WHITE }, sz: 11, name: 'Calibri' },
  fill: { fgColor: { rgb: PRIMARY } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border,
};

const titleStyle = {
  font: { bold: true, color: { rgb: PRIMARY }, sz: 14, name: 'Calibri' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const paramLabelStyle = {
  font: { bold: true, color: { rgb: PARAM_LABEL }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'left' },
};

const paramValueStyle = {
  font: { color: { rgb: '0F172A' }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'left' },
};

const dateInfoStyle = {
  font: { color: { rgb: '64748B' }, sz: 9, name: 'Calibri', italic: true },
  alignment: { horizontal: 'left' },
};

const totalStyle = {
  font: { bold: true, color: { rgb: '0F172A' }, sz: 11, name: 'Calibri' },
  fill: { fgColor: { rgb: TOTAL_BG } },
  border,
};

// Excel number format for an amount column. Supports a per-column `decimals`
// (e.g. 3 for OMR/BHD/KWD) or an explicit `numFmt` override; defaults to 2 decimals.
const amountNumFmt = (col) => {
  if (col.numFmt) return col.numFmt;
  const d = col.decimals ?? 2;
  return d > 0 ? `#,##0.${'0'.repeat(d)}` : '#,##0';
};

const getCellStyle = (col, isAlt) => {
  const base = {
    font: { sz: 10, name: 'Calibri', color: { rgb: '0F172A' } },
    fill: { fgColor: { rgb: isAlt ? ALT_ROW : WHITE } },
    border,
    alignment: { vertical: 'center' },
  };

  if (col.type === 'amount' || col.type === 'number') {
    base.alignment.horizontal = 'right';
    if (col.type === 'amount') {
      base.numFmt = amountNumFmt(col);
    }
  } else {
    base.alignment.horizontal = 'left';
  }

  return base;
};

/**
 * Calculate auto column widths based on header + data content
 */
const calcColWidths = (columns, data) =>
  columns.map((col) => {
    let max = col.header.length;
    data.forEach((row) => {
      const val = row[col.key];
      const len = val != null ? String(val).length : 0;
      if (len > max) max = len;
    });
    // Clamp between 10 and 45, add padding
    const width = Math.max(10, Math.min(45, max + 3));
    return col.width ? { wch: Math.max(col.width, width) } : { wch: width };
  });

/**
 * Export data to a professionally formatted Excel file.
 *
 * @param {Object} options
 * @param {string} options.title - Report title (Row 1)
 * @param {string} options.fileName - Output file name
 * @param {string} options.sheetName - Worksheet tab name
 * @param {Array<{header: string, key: string, width?: number, type?: 'text'|'amount'|'number'|'date', decimals?: number, numFmt?: string}>} options.columns
 *   For `type: 'amount'`, pass `decimals` (e.g. 3 for OMR/BHD/KWD) to control the number format.
 * @param {Array<Object>} options.data - Row data (keys should match column keys)
 * @param {Array<{label: string, value: string}>} [options.parameters] - Filter parameters to display
 * @param {Object} [options.totals] - Totals keyed by column header name, e.g. { 'Amount': 125000 }
 */
export const exportToExcel = ({
  title,
  fileName,
  sheetName = 'Sheet1',
  columns,
  data,
  parameters = [],
  totals,
}) => {
  if (!data.length && !columns.length) return;

  const wb = XLSX.utils.book_new();
  const colCount = columns.length;

  // ── Build rows as AOA (array of arrays) ──
  const aoa = [];
  const styles = {}; // cell ref → style

  // Row 0: Title (merged across all columns)
  aoa.push([title, ...Array(colCount - 1).fill('')]);
  for (let c = 0; c < colCount; c++) {
    styles[XLSX.utils.encode_cell({ r: 0, c })] = titleStyle;
  }

  // Row 1: Generated date
  const now = new Date();
  const dateStr = `Generated: ${now.toLocaleDateString('en-GB')} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  aoa.push([dateStr, ...Array(colCount - 1).fill('')]);
  for (let c = 0; c < colCount; c++) {
    styles[XLSX.utils.encode_cell({ r: 1, c })] = dateInfoStyle;
  }

  // Rows 2+: Parameters
  parameters.forEach((param) => {
    const row = [`${param.label}:`, param.value, ...Array(Math.max(0, colCount - 2)).fill('')];
    const r = aoa.length;
    aoa.push(row);
    styles[XLSX.utils.encode_cell({ r, c: 0 })] = paramLabelStyle;
    styles[XLSX.utils.encode_cell({ r, c: 1 })] = paramValueStyle;
  });

  // Blank separator row
  aoa.push(Array(colCount).fill(''));
  const headerRowIdx = aoa.length;

  // Column headers
  aoa.push(columns.map((col) => col.header));
  for (let c = 0; c < colCount; c++) {
    styles[XLSX.utils.encode_cell({ r: headerRowIdx, c })] = headerStyle;
  }

  // Data rows
  data.forEach((row, i) => {
    const r = aoa.length;
    const isAlt = i % 2 === 1;
    const rowArr = columns.map((col, c) => {
      const val = row[col.key];
      styles[XLSX.utils.encode_cell({ r, c })] = getCellStyle(col, isAlt);

      if (col.type === 'amount' || col.type === 'number') {
        return val != null ? Number(val) || 0 : 0;
      }
      return val != null ? String(val) : '';
    });
    aoa.push(rowArr);
  });

  // Totals row
  if (totals && Object.keys(totals).length > 0) {
    const r = aoa.length;
    const totalRow = columns.map((col, c) => {
      const style = { ...totalStyle };
      if (col.type === 'amount') {
        style.numFmt = amountNumFmt(col);
        style.alignment = { horizontal: 'right', vertical: 'center' };
      } else {
        style.alignment = { horizontal: 'left', vertical: 'center' };
      }
      styles[XLSX.utils.encode_cell({ r, c })] = style;

      if (totals[col.header] !== undefined) {
        return Number(totals[col.header]) || 0;
      }
      if (c === 0) return 'Total';
      return '';
    });
    aoa.push(totalRow);
  }

  // ── Create worksheet ──
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Apply styles
  Object.entries(styles).forEach(([ref, style]) => {
    if (ws[ref]) {
      ws[ref].s = style;
    }
  });

  // Merge title row across all columns
  if (!ws['!merges']) ws['!merges'] = [];
  ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  // Merge date row
  ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });
  // Merge parameter value cells if there's room
  parameters.forEach((_, i) => {
    const r = 2 + i;
    if (colCount > 2) {
      ws['!merges'].push({ s: { r, c: 1 }, e: { r, c: colCount - 1 } });
    }
  });

  // Column widths
  ws['!cols'] = calcColWidths(columns, data);

  // Row heights — title row taller
  ws['!rows'] = [{ hpt: 28 }];

  // Freeze panes — freeze below column headers
  ws['!freeze'] = { xSplit: 0, ySplit: headerRowIdx + 1, topLeftCell: XLSX.utils.encode_cell({ r: headerRowIdx + 1, c: 0 }) };

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};
